if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // A size choice is a head start on the bag's optional photos, without competing with the first page paint.
        this.closest('at-buybox')?.addEventListener('change', () => this.cart?.querySelector('at-suggest')?.warmImages?.(), { once: true });
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // The Atelier: an Add made before the bag drawer's scripts have run (pressed while the page loads, replayed by
        // <at-buybox> once its size is known, which is now at once) waits for them: the drawer is what asks for the
        // bag's sections and paints the answer. Once, however often it is pressed meanwhile.
        if (this.cart && typeof this.cart.getSectionsToRender !== 'function') {
          if (this.waitingForCart) return;
          this.waitingForCart = true;
          const name = this.cart.localName;
          customElements.whenDefined(name).then(() => {
            if (!this.waitingForCart) return;
            this.waitingForCart = false;
            this.onSubmitHandler(evt);
          });
          // If the page has finished loading and the drawer's script still has not defined it, it failed to load: post
          // the form the plain way (the size is already chosen), which adds the dress and opens the bag page.
          const fallback = () => {
            if (!this.waitingForCart || customElements.get(name)) return;
            this.waitingForCart = false;
            this.form.submit();
          };
          if (document.readyState === 'complete') fallback();
          else window.addEventListener('load', fallback, { once: true });
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        // The Atelier: the phone buy bar mirrors the adding state (<at-buybox> in assets/at-product.js).
        this.dispatchEvent(new CustomEvent('at:add-busy', { bubbles: true, detail: { busy: true } }));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        // The Atelier: the bag drawer opens now, with this dress as a pending line (showPending), instead of a second
        // later when the store's answer arrives; the answer then replaces it.
        const pending = this.showPending(formData);

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        // The Atelier: numbered like every bag change, so this answer never paints over a newer bag (cart.js).
        const change = typeof CartItems !== 'undefined' ? CartItems.startChange() : 0;

        // The Atelier: set once the store's answer to the add has arrived and been read. Anything thrown after that
        // (painting the bag drawer, a cartUpdate subscriber) happened after a real add, so it must not say the add failed.
        let answered = false;
        let added = false;
        const inBagBefore = this.bagQuantity(variantId);
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            answered = true;
            added = !response.status;
            if (response.status) {
              pending?.fail(response.description || response.message || (window.cartStrings && window.cartStrings.error));
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              return new Promise((resolve, reject) => {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      try {
                        CartPerformance.measure("add:paint-updated-sections", () => {
                          resolve(this.cart.renderContents(response, change));
                        });
                      } catch (error) {
                        reject(error);
                      }
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              });
            } else if (pending) {
              // The Atelier: complete the pending line's sections first. If they remain unavailable, the bag page
              // shows the accepted Add. A drawer she closed meanwhile is painted and stays closed.
              // renderContents also refreshes an answer overtaken by another change through CartQueue.
              const ids = this.cart.getSectionsToRender().map((section) => section.id);
              return CartItems.withSections(response.sections, ids).then(
                (sections) => {
                  let refreshed;
                  CartPerformance.measure('add:paint-updated-sections', () => {
                    refreshed = this.cart.renderContents({ ...response, sections }, change, !this.cart.classList.contains('active'));
                  });
                  // An overtaken answer is replaced by a fresh bag: the pending line stays until that has painted.
                  return Promise.resolve(refreshed).then(() => pending.settle());
                },
                () => {
                  (CartItems.pendingCartDataPromise || Promise.resolve()).then(() => {
                    window.location = window.routes.cart_url;
                  });
                }
              );
            } else {
              let rendered;
              CartPerformance.measure("add:paint-updated-sections", () => {
                rendered = this.cart.renderContents(response, change);
              });
              // The answer can arrive before the drawer's styles. Keep the button busy until the bag is visible.
              return rendered;
            }
          })
          .catch((e) => {
            console.error(e);
            // The Atelier: a dropped connection used to leave no message (the button simply came back); say so in the
            // same place as the store's own errors, with the bag's own "please try again" line (window.cartStrings).
            // Only when no answer arrived: a glitch after a real add must never invite her to add the same dress twice.
            // If the dress is in the bag but the drawer could not show it, take her to the bag page, which always
            // reflects the add (as cart-drawer.js does when its sections fail), once Shopify's cart:lines-update read
            // has settled so its analytics event is not cut off.
            if (added) {
              if (this.cart && this.cart.classList.contains('active') && !pending) return;
              (typeof CartItems !== 'undefined' && CartItems.pendingCartDataPromise
                ? CartItems.pendingCartDataPromise.catch(() => {})
                : Promise.resolve()
              ).then(() => {
                window.location = window.routes.cart_url;
              });
              return;
            }
            if (answered) return pending?.settle();
            const failAdd = () => {
              pending?.fail(window.cartStrings && window.cartStrings.error);
              this.handleErrorMessage(window.cartStrings && window.cartStrings.error);
              this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
              linesUpdateDeferred?.reject(e);
            };
            // The Atelier: no answer does not mean no add: Shopify may have added the dress and the answer been lost on
            // the way back. Read the bag before offering "try again", which would add a second one. More of this size
            // than before the tap: it went in, so show the bag page. Unchanged, or the bag cannot be read: say it failed.
            if (inBagBefore === null) return failAdd();
            return fetch(`${routes.cart_url}.js`, { cache: 'no-store' })
              .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Bag check unavailable'))))
              .then((cart) => {
                const now = (cart.items || [])
                  .filter((item) => String(item.variant_id) === String(variantId))
                  .reduce((sum, item) => sum + item.quantity, 0);
                if (now > inBagBefore) window.location = window.routes.cart_url;
                else failAdd();
              }, failAdd);
          })
          .finally(async () => {
            if (added) await this.cart?.opening;
            this.submitButton.classList.remove('loading');
            // The Atelier: only an add the store accepted fills the bag. A refused or failed add into an empty bag keeps
            // its empty message and Continue shopping (and the next pending line goes where an empty bag puts it).
            if (added && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.dispatchEvent(new CustomEvent('at:add-busy', { bubbles: true, detail: { busy: false } }));

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // The Atelier: how many of this variant the bag drawer shows now (line keys start with the variant id), so a lost
      // answer can be checked against the store's bag. Null when there is no drawer to read.
      bagQuantity(variantId) {
        if (!this.cart || this.cart.localName !== 'cart-drawer') return null;
        return [...this.cart.querySelectorAll('.quantity__input[data-quantity-line-key]')]
          .filter((box) => box.dataset.quantityLineKey.split(':')[0] === String(variantId))
          .reduce((sum, box) => sum + (Number(box.defaultValue) || 0), 0);
      }

      // The Atelier: the pending bag line. When Add to bag is pressed and the bag drawer is closed, the drawer opens at
      // once with the dress in it: its photo (the product page's own loaded photo, over the bag line's file, which starts
      // loading now so the real line's photo is ready too), name, size and price, and "Adding to your bag…" with a
      // quiet moving hairline where the quantity will be. It goes first, where Shopify puts a new line; if the bag
      // already holds that size it is that line that shows it (Shopify adds one to it). The bag keeps every add still on
      // its way (AtPendingAdds below): each keeps its line through any repaint of the drawer, the rest of the bag (lines,
      // suggestions, note, Checkout) stays inert until the last of them has settled, and each is settled by its own
      // request. A refusal or a dropped connection takes that line out and puts the message in its place (role=alert)
      // until the drawer closes, and says it by the button as before. Only for a form that can describe its line: the
      // product page's (at-buybox describeLine) and Pairs well with rows; the bag's own suggestion rows add inside the
      // open drawer as before. Returns null when there is nothing to show.
      showPending(formData) {
        const drawer = this.cart;
        if (!drawer || drawer.localName !== 'cart-drawer' || drawer.classList.contains('active')) return null;
        // An Add pressed before the drawer's scripts have run (held by buy-buttons, replayed by <at-buybox>) waits for
        // the answer as before: the drawer element is not upgraded yet.
        if (typeof drawer.open !== 'function' || typeof drawer.renderContents !== 'function' || typeof CartItems === 'undefined') return null;
        // A drawer stylesheet failed: the drawer sends her to the bag page instead of opening (cart-drawer.js). Doing that
        // now would cancel this add mid-request, so it waits for the answer, which opens the bag as usual.
        if (document.querySelector('link[data-at-drawer-css][data-failed]')) return null;
        if (this.closest('quick-add-modal')) return null;
        let line = null;
        try {
          line = this.describeLine(formData);
        } catch (e) {
          line = null;
        }
        if (!line || !line.title) return null;
        return AtPendingAdds.start(drawer, line);
      }

      // The Atelier: what the pending line shows: { id, title, option, price, img, img2 (the bag line's 168w / 252w
      // files), shown (a loaded photo of the same product to paint at once), properties }. The product page's form asks
      // its <at-buybox>; a Pairs well with row reads itself.
      describeLine(formData) {
        const id = formData.get('id');
        if (!id) return null;
        const properties = Array.from(formData.keys()).some((key) => /^properties\[/.test(key));
        const box = this.closest('at-buybox');
        if (box && box.productForm === this && typeof box.describeLine === 'function') {
          const line = box.describeLine(id);
          return line && { ...line, properties };
        }
        const row = this.closest('.at-pair');
        if (!row) return null;
        const select = row.querySelector('.at-pair__select');
        const chosen = select && select.selectedOptions[0];
        const priceEl = row.querySelector('.at-pair__price');
        let price = '';
        if (priceEl) {
          const copy = priceEl.cloneNode(true);
          copy.querySelectorAll('s').forEach((s) => s.remove());
          price = copy.textContent.split('·')[0].trim();
        }
        let option = '';
        if (chosen && chosen.value) {
          const parts = chosen.textContent.split('·');
          option = parts[0].trim();
          if (parts[1]) price = parts[1].trim();
        } else if (priceEl && priceEl.textContent.includes('·')) {
          option = priceEl.textContent.split('·').pop().trim();
        }
        const pic = row.querySelector('.at-pair__pic img');
        return {
          id,
          title: (row.querySelector('.at-pair__name')?.textContent || '').trim(),
          option,
          price,
          img: row.dataset.lineImg || null,
          img2: row.dataset.lineImg2 || null,
          shown: pic && pic.complete && pic.naturalWidth ? pic.currentSrc : null,
          properties,
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.dataset.atLabelAdd || window.variantStrings.addToCart;
        }
        // Atelier: tells <at-buybox> (the phone bar) the button's settled state. "final" is false only for the
        // disable-while-loading that precedes a size re-render; every other call (Add to bag, Sold out,
        // Unavailable after a failed re-render) is what the button will keep showing.
        this.dispatchEvent(new CustomEvent('at:submit-state', { bubbles: true, detail: { final: !disable || !!text } }));
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/* The Atelier: the adds still on their way to the store, for the bag drawer (one list per page). Each add has its
   pending line in the drawer (product-form.js showPending). A repaint of the drawer from another answer (an earlier
   add, a bag change) replaces its HTML: the lines of the adds still out are put back at once (a MutationObserver on the
   drawer, only while an add is out; its callback runs before the next paint) and the rest of the bag is made inert
   again. Only when the last add has settled does the bag become usable (Checkout, quantities, suggestions, note) and
   its foot undimmed. settle(add) and fail(add, message) end one add, by its own request. */
window.AtPendingAdds = window.AtPendingAdds || {
  adds: new Set(),
  HELD: '.drawer__cart-items-wrapper, at-suggest, .at-drawer-note, .drawer__footer, .cart-drawer__collection',

  start(drawer, line) {
    drawer.querySelectorAll('.at-add-error').forEach((note) => note.remove());
    const add = { drawer, line, el: null, row: null, note: null };
    this.adds.add(add);
    this.place(add);
    this.hold(drawer);
    drawer.open();
    return { settle: () => this.end(add), fail: (message) => this.end(add, message) };
  },

  // Its line where Shopify will put it: the line of the same size (no line properties) if the bag has it, else first.
  place(add) {
    const { drawer, line } = add;
    const noteText = drawer.dataset.adding;
    const row =
      !line.properties &&
      Array.from(drawer.querySelectorAll('.cart-item:not(.at-pending-line) [data-quantity-variant-id]'))
        .find((input) => input.dataset.quantityVariantId === String(line.id))
        ?.closest('.cart-item');
    if (row) {
      add.el?.remove();
      add.el = null;
      add.row = row;
      row.classList.add('at-line-adding');
      if (!add.note || !row.contains(add.note)) {
        add.note = document.createElement('p');
        add.note.className = 'at-pending-note';
        add.note.textContent = noteText;
        row.querySelector('.cart-item__details')?.appendChild(add.note);
      }
      return;
    }
    add.row = null;
    if (!add.el) {
      const el = document.createElement('div');
      el.className = 'cart-item at-pending-line';
      el.dataset.atVariantId = line.id;
      el.innerHTML =
        '<div class="cart-item__media"><img class="cart-item__image" alt="" width="84" height="105" decoding="async"></div>' +
        '<div class="cart-item__details"><div class="cart-item__title"><span class="cart-item__name"></span></div></div>' +
        '<div class="cart-item__totals right"><div class="cart-item__price-wrapper"><span class="price price--end"></span></div></div>';
      el.querySelector('.cart-item__name').textContent = line.title;
      const details = el.querySelector('.cart-item__details');
      if (line.option) {
        const option = document.createElement('p');
        option.className = 'product-option';
        option.textContent = line.option;
        details.appendChild(option);
      }
      const note = document.createElement('p');
      note.className = 'at-pending-note';
      note.textContent = noteText;
      details.appendChild(note);
      el.querySelector('.price').textContent = line.price || '';
      const img = el.querySelector('img');
      if (line.shown) img.style.backgroundImage = `url("${String(line.shown).replace(/"/g, '%22')}")`;
      if (line.img) {
        img.sizes = '72px';
        img.srcset = line.img2 ? `${line.img} 168w, ${line.img2} 252w` : `${line.img} 168w`;
        img.src = line.img;
      } else if (line.shown) {
        img.src = line.shown;
      } else {
        img.remove();
      }
      add.el = el;
    }
    if (add.el.isConnected) return;
    const empty = drawer.classList.contains('is-empty') && drawer.querySelector('.drawer__inner-empty');
    const lines = drawer.querySelector('#CartDrawer-CartItems');
    const list = lines && lines.querySelector('.drawer__cart-items-wrapper');
    const first = drawer.querySelector('.at-pending-line');
    if (first) first.before(add.el);
    else if (empty) empty.querySelector('.drawer__header')?.after(add.el);
    else if (list) list.before(add.el);
    else lines?.prepend(add.el);
  },

  // The rest of the bag waits; watched so a repaint cannot drop a pending line or free Checkout early.
  hold(drawer) {
    drawer.classList.add('at-adding');
    drawer.querySelectorAll(this.HELD).forEach((node) => {
      if (node.inert) return;
      node.inert = true;
      node.dataset.atHeld = '';
    });
    if (!this.watch) {
      this.watch = new MutationObserver(() => {
        if (!this.adds.size) return;
        this.watch.takeRecords();
        this.adds.forEach((add) => this.place(add));
        this.hold(drawer);
        this.watch.takeRecords();
      });
      this.watch.observe(drawer, { childList: true, subtree: true });
    }
  },

  end(add, message) {
    if (!this.adds.delete(add)) return;
    const { drawer } = add;
    const at = add.el && add.el.isConnected ? add.el : add.row && add.row.isConnected ? add.row : null;
    if (message && drawer.classList.contains('active')) {
      const alert = document.createElement('p');
      alert.className = 'at-add-error';
      alert.setAttribute('role', 'alert');
      alert.textContent = message;
      const empty = drawer.classList.contains('is-empty') && drawer.querySelector('.drawer__inner-empty');
      if (at) at.before(alert);
      else if (empty) empty.querySelector('.drawer__header')?.after(alert);
      else drawer.querySelector('#CartDrawer-CartItems')?.prepend(alert);
      // Gone when the drawer closes, so a later look in the bag does not show an old message.
      const closed = new MutationObserver(() => {
        if (drawer.classList.contains('active')) return;
        closed.disconnect();
        alert.remove();
      });
      closed.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    add.el?.remove();
    add.note?.remove();
    if (add.row && !Array.from(this.adds).some((other) => other.row === add.row)) add.row.classList.remove('at-line-adding');
    if (this.adds.size) {
      // Another add is still out: its line (dropped if this answer repainted the drawer) and the hold stay.
      this.adds.forEach((other) => this.place(other));
      this.hold(drawer);
      if (this.watch) this.watch.takeRecords();
      return;
    }
    if (this.watch) {
      this.watch.disconnect();
      this.watch = null;
    }
    drawer.classList.remove('at-adding');
    drawer.querySelectorAll('[data-at-held]').forEach((node) => {
      node.inert = false;
      delete node.dataset.atHeld;
    });
  },
};
